import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Request builders for the SLR loan-standing-verification write paths.
 *
 * Document upload hits a root-namespaced REST route (NOT `/api/v2`, NOT
 * JSON:API) that is still pending on the backend, and returns a plain JSON
 * payload the caller casts to the relevant
 * `tio-employee/types/slr-loan-standing-verification` shape. Name-match
 * confirmation is a JSON:API PATCH of the `employee_plan_accounts` resource
 * (tio-api#8054) — `name_match_confirmation` is an eval-trigger attribute, so
 * the PATCH re-evaluates good standing server-side.
 */

const employeePlanAccountUrl = (path: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'employee-plan-accounts' },
    op: 'query',
    resourcePath: 'employee-plan-accounts',
  };
  return `${buildBaseURL(opts)}/${path.replace(/^\/+/, '')}`;
};

/**
 * POST /employee-plan-accounts/:id/slr-loan-standing-verification/documents —
 * attach a signed verification document.
 *
 * `asset` is the `DigitalAssetDetailSignature` the caller already holds; it is
 * typed as `object` here because tio-data must not depend on tio-common (the
 * dependency arrow points the other way). It is serialized as `{ asset }`.
 */
export function uploadSlrLoanStandingDocumentRequest(
  employeePlanAccountId: string,
  asset: object
): RequestInfo {
  return {
    url: employeePlanAccountUrl(
      `${employeePlanAccountId}/slr-loan-standing-verification/documents`
    ),
    method: 'POST',
    body: JSON.stringify({ asset }),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /employee-plan-accounts/:id — record the participant's answer to the
 * fuzzy name-match prompt (`good_standing_reason ==
 * 'name_match_confirmation_required'`). Setting `name_match_confirmation`
 * triggers an inline server-side re-evaluation: `confirmed` lets the name
 * check pass; `denied` resolves to ineligible/name_mismatch. Note the PATCH
 * response is rendered from the pre-evaluation record — callers should
 * refetch for the settled state.
 *
 * Member names are CAMELIZED, not dasherized: the handler chain sends
 * `x-json-api-key-format: camelized`, and tio-api honors that header for
 * request-body parsing too — a dasherized `name-match-confirmation` is
 * silently dropped as "Param not allowed" (200 + meta warning, no-op).
 */
export function confirmSlrLoanStandingNameMatchRequest(
  employeePlanAccountId: string,
  confirmation: 'confirmed' | 'denied'
): RequestInfo {
  return {
    url: employeePlanAccountUrl(employeePlanAccountId),
    method: 'PATCH',
    body: JSON.stringify({
      data: {
        type: 'employeePlanAccounts',
        id: employeePlanAccountId,
        attributes: { nameMatchConfirmation: confirmation },
      },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}
